import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStakingData } from '../../../hooks/useStakingData';
import { Card } from '../../shared/Card';
import { ExternalLink, AlertTriangle, Check, X } from 'lucide-react';
import { formatDate } from '../../../lib/utils';
import { formatEther } from 'viem';
import type { Collection } from '../../../types';

interface NFTDetailsProps {
  collection: Collection;
  tokenId: string;
}

export function NFTDetails({ collection, tokenId }: NFTDetailsProps) {
  const { data: stakingInfo, isLoading: isStakingLoading } = useStakingData(collection, tokenId);
  
  const { 
    data: listingData,
    isLoading: isListingLoading,
    isError: isListingError
  } = useQuery({
    queryKey: ['nft-listing', collection.address, tokenId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `https://api-mainnet.magiceden.dev/v3/rtp/apechain/tokens/v6?tokens=${collection.address}%3A${tokenId}&sortBy=floorAskPrice&limit=20&includeTopBid=false&excludeEOA=false&includeAttributes=false&includeQuantity=false&includeDynamicPricing=false&includeLastSale=false&normalizeRoyalties=false`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch token data');
        }

        const data = await response.json();
        const token = data.tokens?.[0];

        if (!token) {
          return { isListed: false };
        }

        return {
          isListed: Boolean(token.market?.floorAsk?.price)
        };
      } catch (error) {
        console.error('Error fetching token data:', error);
        throw error;
      }
    },
    retry: 2,
    staleTime: 30_000 // Consider data stale after 30 seconds
  });

  // Generate Magic Eden NFT link
  const magicEdenNFTLink = `https://magiceden.us/item-details/apechain/${collection.address}/${tokenId}`;

  if (isStakingLoading || isListingLoading) {
    return (
      <Card className="mt-4">
        <Card.Content>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-blue-500/10 rounded-lg w-1/2" />
            <div className="h-12 bg-blue-500/10 rounded-lg" />
          </div>
        </Card.Content>
      </Card>
    );
  }

  if (isListingError) {
    return (
      <Card className="mt-4">
        <Card.Content>
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Error Loading NFT Data</p>
                <p className="text-red-400/80 text-sm mt-1">
                  Failed to fetch NFT data. Please try again later.
                </p>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <Card.Content className="space-y-4">
        {/* Staking Status */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Status</span>
            <span className={`font-medium ${stakingInfo?.isStaked ? 'text-green-400' : 'text-blue-400'}`}>
              {stakingInfo?.isStaked ? 'Staked' : 'Not Staked'}
            </span>
          </div>

          {stakingInfo?.isStaked && stakingInfo.startDate && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Start Date</span>
                <span className="text-white">{formatDate(stakingInfo.startDate)}</span>
              </div>
              
              {stakingInfo.endDate && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">End Date</span>
                  <span className="text-white">{formatDate(stakingInfo.endDate)}</span>
                </div>
              )}

              {stakingInfo.reward && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Reward</span>
                  <span className="text-white font-medium">
                    {Math.floor(Number(formatEther(stakingInfo.reward)))} $GEM
                  </span>
                </div>
              )}

              {stakingInfo.pendingRewards && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Pending Rewards</span>
                  <span className="text-green-400 font-medium">
                    {Math.floor(Number(formatEther(stakingInfo.pendingRewards)))} $GEM
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Progress Bar */}
        {stakingInfo?.isStaked && stakingInfo.startDate && stakingInfo.endDate && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Staking Progress</span>
              <span className="text-blue-400">
                {Math.min(
                  100,
                  Math.floor(
                    ((Date.now() - stakingInfo.startDate.getTime()) /
                      (stakingInfo.endDate.getTime() - stakingInfo.startDate.getTime())) *
                      100
                  )
                )}%
              </span>
            </div>
            <div className="h-2 bg-blue-500/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.min(
                    100,
                    ((Date.now() - stakingInfo.startDate.getTime()) /
                      (stakingInfo.endDate.getTime() - stakingInfo.startDate.getTime())) *
                      100
                  )}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Listing Status */}
        <div className="bg-blue-500/10 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Listing Status</span>
            <div className="flex items-center gap-2">
              {listingData?.isListed ? (
                <>
                  <span className="text-green-400 font-medium">Listed</span>
                  <Check className="w-4 h-4 text-green-400" />
                </>
              ) : (
                <>
                  <span className="text-red-400 font-medium">Not Listed</span>
                  <X className="w-4 h-4 text-red-400" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Magic Eden Link */}
        <a
          href={magicEdenNFTLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-xl text-blue-400 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          View on Magic Eden
        </a>
      </Card.Content>
    </Card>
  );
}